import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Eye, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { Toast } from '../../components/ui/Toast';
import { ProtectedAction } from '../../components/ProtectedAction';
import { PropertyForm } from '../../components/forms/PropertyForm';
import { api, getPropertyCoverImageUrl, PROPERTY_IMAGE_PLACEHOLDER } from '../../services/api';
import { ACCESS_PROPERTIES, usePermissions } from '../../hooks/usePermissions';
import { Property, PropertiesSummary } from '../../types';

function transformProperty(property: any): Property {
  const documents = (property.documents || property.property_documents || []).map((doc: any) => ({
    id: doc.id,
    title: doc.title,
    fileUrl: doc.fileUrl,
    fileType: doc.fileType,
    uploadedAt: doc.uploadedAt || doc.createdAt || new Date().toISOString(),
  }));
  return {
    id: property.id,
    name: property.name,
    address: property.address,
    city: property.city,
    state: property.state || property.estado || '',
    unitCount: property.unitCount || 0,
    occupiedUnits: property.occupiedUnits || property.tenants?.length || 0,
    m2Total: property.m2Total || 0,
    pricePerM2: property.pricePerM2 || 0,
    totalValue: property.totalValue || 0,
    currentValue:
      property.currentValue != null ? Number(property.currentValue) : null,
    status: property.status || 'en-proyecto',
    coverImage: property.coverImage ?? property.cover_image ?? undefined,
    documents,
    createdAt: property.createdAt || new Date().toISOString(),
  };
}

/** Fallback si falla GET /properties/summary (misma lógica previa del listado; ocupación no ponderada). */
function computeClientSummary(properties: Property[]): PropertiesSummary {
  const totalPortfolioValue = properties.reduce((sum, p) => {
    const value = typeof p.totalValue === 'number' ? p.totalValue : parseFloat(String(p.totalValue)) || 0;
    return sum + value;
  }, 0);
  const averageOccupancy =
    properties.length > 0
      ? properties.reduce(
          (sum, p) => sum + (p.unitCount > 0 ? (p.occupiedUnits / p.unitCount) * 100 : 0),
          0,
        ) / properties.length
      : 0;
  return {
    totalProperties: properties.length,
    totalPortfolioValue,
    averageOccupancy: Math.round(averageOccupancy * 10) / 10,
    totalUnits: 0,
    occupiedUnits: 0,
  };
}

export const PropertiesList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'en-proyecto' | 'en-construccion' | 'en-remodelacion' | 'operativo'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPropertyId, setEditingPropertyId] = useState<string | null>(null);
  const [editingProperty, setEditingProperty] = useState<Property | undefined>(undefined);
  const [editingPropertyUnits, setEditingPropertyUnits] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState<Property | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const { hasPermission } = usePermissions();
  const canViewPrices = hasPermission('view_prices');

  const { data: propertiesRes, isLoading: loadingList, isFetching: fetchingList, error: queryError } = useQuery<{
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }>({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
  });

  const {
    data: summaryFromApi,
    isError: summaryQueryError,
    isSuccess: summaryQuerySuccess,
  } = useQuery<PropertiesSummary>({
    queryKey: ['properties', 'summary'],
    queryFn: () => api.getPropertiesSummary(),
    staleTime: 60_000,
  });
  const allProperties: Property[] = useMemo(() => {
    const raw = propertiesRes?.data;
    const propertiesData: any[] = Array.isArray(raw) ? raw : [];
    return propertiesData
      .filter((property: any) => property.status !== 'eliminado')
      .map((p: any) => transformProperty(p))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [propertiesRes]);
  const error = mutationError || (queryError ? (queryError as Error)?.message ?? 'Error al cargar los datos' : null);

  const { data: fullPropertyData, isLoading: editingDataLoading } = useQuery<any>({
    queryKey: ['property', editingPropertyId],
    queryFn: () => api.getProperty(editingPropertyId!),
    enabled: !!editingPropertyId,
    staleTime: 60_000,
  });
  const { data: unitsData } = useQuery<any[] | { data: any[] }>({
    queryKey: ['units', editingPropertyId],
    queryFn: () => api.getUnits(editingPropertyId!),
    enabled: !!editingPropertyId,
    staleTime: 60_000,
  });
  React.useEffect(() => {
    if (fullPropertyData) setEditingProperty(transformProperty(fullPropertyData));
    else if (!editingPropertyId) setEditingProperty(undefined);
  }, [fullPropertyData, editingPropertyId]);
  React.useEffect(() => {
    const raw = unitsData;
    const list: any[] = Array.isArray(raw)
      ? raw
      : raw && Array.isArray((raw as any).data)
      ? (raw as any).data
      : [];
    setEditingPropertyUnits(list);
  }, [unitsData]);

  const deletePropertyMutation = useMutation({
    mutationFn: (id: string) => api.deleteProperty(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setIsDeleteModalOpen(false);
      setPropertyToDelete(null);
      setToast({ message: 'Propiedad eliminada', type: 'success' });
    },
    onError: (err: Error) => {
      setMutationError(err.message || 'Error al eliminar la propiedad');
    },
  });

  // Memoizar las ciudades disponibles (solo se recalcula cuando cambian las propiedades)
  const cities = useMemo(() => {
    return Array.from(new Set(allProperties.map((p) => p.city))).sort();
  }, [allProperties]);

  // Filtrar propiedades localmente (muy rápido, no hace llamadas al backend)
  const properties = useMemo(() => {
    // Si no hay filtros activos, devolver todas las propiedades sin copiar
    if (!searchTerm.trim() && cityFilter === 'all' && statusFilter === 'all') {
      return allProperties;
    }

    let filtered = allProperties;

    // Filtro por estado primero (más rápido)
    if (statusFilter !== 'all') {
      filtered = filtered.filter((property) => property.status === statusFilter);
    }

    // Filtro por ciudad
    if (cityFilter !== 'all') {
      filtered = filtered.filter((property) => property.city === cityFilter);
    }

    // Filtro de búsqueda (nombre o dirección) - aplicarlo al final para reducir el conjunto
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(
        (property) =>
          property.name.toLowerCase().includes(searchLower) ||
          property.address.toLowerCase().includes(searchLower)
      );
    }

    // Asegurar que el orden se mantenga del más nuevo al más antiguo
    return filtered.sort((a, b) => {
      const dateA = new Date(a.createdAt).getTime();
      const dateB = new Date(b.createdAt).getTime();
      return dateB - dateA; // Orden descendente (más nuevo primero)
    });
  }, [allProperties, searchTerm, cityFilter, statusFilter]);

  const clientSummary = useMemo(() => computeClientSummary(allProperties), [allProperties]);

  const summaryForCards = useMemo((): PropertiesSummary => {
    if (summaryQuerySuccess && summaryFromApi) return summaryFromApi;
    if (summaryQueryError) return clientSummary;
    return clientSummary;
  }, [summaryQuerySuccess, summaryQueryError, summaryFromApi, clientSummary]);

  const handleOpenModal = (property?: Property) => {
    setMutationError(null);
    if (property) {
      setEditingPropertyId(property.id);
      setEditingProperty(undefined);
      setEditingPropertyUnits([]);
    } else {
      setEditingPropertyId(null);
      setEditingProperty(undefined);
      setEditingPropertyUnits([]);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPropertyId(null);
    setEditingProperty(undefined);
    setEditingPropertyUnits([]);
    setMutationError(null);
  };

  const handleSubmit = async (
    propertyData: Omit<Property, 'id' | 'createdAt' | 'documents'> & { documents?: any[] },
    units?: any[]
  ) => {
    try {
      setMutationError(null);
      setIsSubmitting(true);
      
      // Preparar datos para enviar (sin documentos, estado y occupiedUnits)
      const { documents, ...restData } = propertyData;
      
      // Eliminar campos que no están en los DTOs del backend
      const propertyPayload: any = { ...restData };
      delete propertyPayload.estado;
      delete propertyPayload.occupiedUnits;
      // coverImage va en el payload (base64 o ruta existente)
      
      // Asegurar que el status tenga un valor válido
      const validStatuses = ['en-proyecto', 'en-construccion', 'en-remodelacion', 'operativo', 'eliminado'];
      if (!propertyPayload.status || !validStatuses.includes(propertyPayload.status)) {
        // Si no hay status o es inválido, usar el default o el status actual de la propiedad
        propertyPayload.status = editingProperty?.status || 'en-proyecto';
      }
      
      // Debug: ver qué se está enviando
      console.log('Property payload being sent:', JSON.stringify(propertyPayload, null, 2));
      
      if (editingProperty) {
        // Editar propiedad existente
        const updatedProperty = await api.updateProperty(editingProperty.id, propertyPayload);
        
        // Guardar/actualizar documentos
        if (documents && documents.length > 0) {
          try {
            // Obtener documentos existentes
            const existingProperty = await api.getProperty(editingProperty.id);
            const existingDocuments = existingProperty.documents || existingProperty.property_documents || [];
            const existingDocsMap = new Map(existingDocuments.map((d: any) => [d.id, d]));
            
            // Procesar cada documento del formulario
            for (const doc of documents) {
              // Si el documento tiene un ID real (UUID) y existe en la base de datos, ya existe
              // Los IDs temporales (que empiezan con "doc-") deben crearse siempre
              const isTemporaryId = doc.id && doc.id.toString().startsWith('doc-');
              if (doc.id && !isTemporaryId && existingDocsMap.has(doc.id)) {
                // El documento ya existe, no hacer nada (o actualizar si es necesario)
                continue;
              } else {
                // Crear nuevo documento (ya sea porque no tiene ID, tiene ID temporal, o no existe en la BD)
                await api.addPropertyDocument(editingProperty.id, {
                  title: doc.title,
                  fileUrl: doc.fileUrl,
                  fileType: doc.fileType,
                });
              }
            }
            
            // Eliminar documentos que ya no están en el formulario
            const formDocIds = new Set(documents.filter((d: any) => d.id).map((d: any) => d.id));
            for (const existingDoc of existingDocuments) {
              if (!formDocIds.has(existingDoc.id)) {
                try {
                  await api.deletePropertyDocument(existingDoc.id);
                } catch (deleteErr) {
                  console.error('Error deleting document:', deleteErr);
                }
              }
            }
          } catch (docsErr) {
            console.error('Error saving documents:', docsErr);
            // No fallar todo si solo falla el guardado de documentos
          }
        }
        
        // Guardar/actualizar unidades si se proporcionaron
        if (units && units.length > 0) {
          // Obtener unidades existentes de la propiedad
          const unitsRaw = await api.getUnits(editingProperty.id);
          const existingUnitsList: any[] = Array.isArray(unitsRaw) ? unitsRaw : (unitsRaw as any)?.data ?? [];
          const existingUnitsMap = new Map(existingUnitsList.map((u: any) => [u.unitNumber, u]));

          // Procesar cada unidad del formulario
          for (const unit of units) {
            const existingUnit = existingUnitsMap.get(unit.unitNumber) as { id: string; unitNumber: string } | undefined;
            const { propertyType, ...unitData } = unit as any;

            if (existingUnit) {
              await api.updateUnit(existingUnit.id, {
                ...unitData,
                propertyId: editingProperty.id,
              });
            } else {
              await api.createUnit({
                ...unitData,
                propertyId: editingProperty.id,
              });
            }
          }

          const formUnitNumbers = new Set(units.map((u: any) => u.unitNumber));
          for (const existingUnit of existingUnitsList) {
            if (!formUnitNumbers.has(existingUnit.unitNumber)) {
              await api.deleteUnit(existingUnit.id);
            }
          }
        }
        
        // Recargar la propiedad completa para obtener documentos e imagen actualizados
        const fullProperty = await api.getProperty(editingProperty.id);
        
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        queryClient.invalidateQueries({ queryKey: ['property', editingPropertyId] });
        setToast({ message: 'Propiedad actualizada exitosamente', type: 'success' });
        setTimeout(() => handleCloseModal(), 0);
      } else {
        // Crear nueva propiedad
        const newProperty = await api.createProperty(propertyPayload);
        
        // Guardar documentos si se proporcionaron
        if (documents && documents.length > 0) {
          try {
            for (const doc of documents) {
              await api.addPropertyDocument(newProperty.id, {
                title: doc.title,
                fileUrl: doc.fileUrl,
                fileType: doc.fileType,
              });
            }
          } catch (docsErr) {
            console.error('Error saving documents:', docsErr);
            // No fallar todo si solo falla el guardado de documentos
          }
        }
        
        // Guardar unidades si se proporcionaron
        if (units && units.length > 0) {
          for (const unit of units) {
            // Filtrar campos permitidos por el backend (eliminar propertyType)
            const { propertyType, ...unitData } = unit as any;
            await api.createUnit({
              ...unitData,
              propertyId: newProperty.id,
            });
          }
        }
        
        // Recargar la propiedad completa para obtener documentos e imagen
        const fullProperty = await api.getProperty(newProperty.id);
        
        queryClient.invalidateQueries({ queryKey: ['properties'] });
        setToast({ message: 'Propiedad agregada exitosamente', type: 'success' });
        setTimeout(() => handleCloseModal(), 0);
      }
    } catch (err: any) {
      console.error('Error saving property:', err);
      const errorMessage = err.message || 'Error al guardar la propiedad';
      setMutationError(errorMessage);
      setToast({ message: errorMessage, type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (property: Property) => {
    setPropertyToDelete(property);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!propertyToDelete) return;
    setDeletingId(propertyToDelete.id);
    setMutationError(null);
    deletePropertyMutation.mutate(propertyToDelete.id, {
      onSettled: () => setDeletingId(null),
    });
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setPropertyToDelete(null);
  };

  const columns: Column<Property>[] = [
    {
      header: 'Imagen',
      accessor: (row) => (
        <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200">
          {row.coverImage ? (
            <img
              src={getPropertyCoverImageUrl(row.coverImage)}
              alt={row.name}
              className="w-full h-full object-cover"
              loading="lazy"
              decoding="async"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = PROPERTY_IMAGE_PLACEHOLDER;
              }}
            />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-xs text-gray-400">Sin imagen</span>
            </div>
          )}
        </div>
      ),
      className: 'w-20',
    },
    { header: 'Nombre', accessor: 'name', sortable: true },
    { header: 'Dirección', accessor: 'address' },
    { 
      header: 'Ciudad', 
      accessor: (row) => {
        if (row.state && row.city) {
          return `${row.state} / ${row.city}`;
        }
        return row.city || row.state || '-';
      },
      sortable: true 
    },
    {
      header: 'Unidades',
      accessor: (row) => `${row.occupiedUnits}/${row.unitCount}`,
    },
    {
      header: 'Ocupación',
      accessor: (row) => {
        // Manejar caso cuando unitCount es 0 o el cálculo resulta en NaN
        const pct = row.unitCount > 0 
          ? Math.round((row.occupiedUnits / row.unitCount) * 100)
          : 0;
        const percentage = isNaN(pct) ? 0 : pct;
        return (
          <div className="flex items-center space-x-2">
            <span>{percentage}%</span>
            <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      },
    },
    {
      header: 'Valor Total',
      accessor: (row) => {
        if (!canViewPrices) return '—';
        const value = typeof row.totalValue === 'number' ? row.totalValue : parseFloat(row.totalValue) || 0;
        return value.toLocaleString('es-MX', {
          style: 'currency',
          currency: 'MXN',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      },
      sortable: true,
    },
    {
      header: 'Estado',
      accessor: (row) => {
        const statusConfig = {
          'en-proyecto': { label: 'En Proyecto', className: 'bg-yellow-100 text-yellow-800' },
          'en-construccion': { label: 'En Construcción', className: 'bg-orange-100 text-orange-800' },
          'en-remodelacion': { label: 'En Remodelación', className: 'bg-purple-100 text-purple-800' },
          'operativo': { label: 'Operativo', className: 'bg-green-100 text-green-800' },
        };
        const config = statusConfig[row.status] || { label: row.status, className: 'bg-gray-100 text-gray-800' };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      header: 'Acciones',
      accessor: (row) => (
        <div className="flex items-center space-x-2">
          <ProtectedAction permission={[...ACCESS_PROPERTIES]}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/propiedades/${row.id}`);
              }}
              className="text-primary hover:text-primary/80 p-1"
              title="Ver detalle"
              aria-label="Ver detalle de la propiedad"
            >
              <Eye className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission={['update_properties', 'manage_properties']}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenModal(row);
              }}
              className="text-gray-600 hover:text-gray-800 p-1"
              title="Editar"
              aria-label="Editar propiedad"
            >
              <Edit className="w-4 h-4" />
            </button>
          </ProtectedAction>
          <ProtectedAction permission="delete_properties">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
              disabled={deletingId === row.id}
              className="text-red-500 hover:text-red-700 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Eliminar"
              aria-label="Eliminar propiedad"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </ProtectedAction>
        </div>
      ),
    },
  ];

  if (loadingList && !propertiesRes) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Propiedades</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse" />
            </Card>
          ))}
        </div>
        <div className="flex items-center justify-center py-16 bg-white border border-gray-200 rounded-lg">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4" />
            <p className="text-gray-600">Cargando propiedades...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Propiedades</h1>
        <ProtectedAction permission={['create_properties', 'manage_properties']}>
          <Button icon={Plus} onClick={() => handleOpenModal()}>
            Agregar Propiedad
          </Button>
        </ProtectedAction>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex flex-wrap items-center justify-between gap-2">
          <span>{error}</span>
          <Button variant="secondary" size="sm" onClick={() => queryClient.invalidateQueries({ queryKey: ['properties'] })}>
            Reintentar
          </Button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <div className="text-sm text-gray-600 mb-1">Total Propiedades</div>
          <div className="text-2xl font-bold text-primary">{summaryForCards.totalProperties}</div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Valor Total Portafolio</div>
          <div className="text-2xl font-bold text-primary">
            {canViewPrices
              ? summaryForCards.totalPortfolioValue.toLocaleString('es-MX', {
                  style: 'currency',
                  currency: 'MXN',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })
              : '—'}
          </div>
        </Card>
        <Card>
          <div className="text-sm text-gray-600 mb-1">Ocupación Promedio</div>
          <div className="text-2xl font-bold text-primary">{summaryForCards.averageOccupancy.toFixed(1)}%</div>
          {summaryForCards.totalUnits > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {summaryForCards.occupiedUnits} de {summaryForCards.totalUnits} unidades
            </p>
          )}
        </Card>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Buscar por nombre o dirección..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
            />
          </div>
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todas las ciudades</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="all">Todos los estados</option>
            <option value="en-proyecto">En Proyecto</option>
            <option value="en-construccion">En Construcción</option>
            <option value="en-remodelacion">En Remodelación</option>
            <option value="operativo">Operativo</option>
          </select>
        </div>

        {properties.length === 0 && !loadingList ? (
          <div className="text-center py-12">
            <p className="text-gray-500">
              {searchTerm || cityFilter !== 'all' || statusFilter !== 'all'
                ? 'No se encontraron propiedades que coincidan con los filtros'
                : 'No hay propiedades registradas'}
            </p>
          </div>
        ) : (
          <div className={fetchingList ? 'opacity-60 pointer-events-none' : ''}>
            <Table
              data={properties}
              columns={columns}
              onRowClick={(row) => navigate(`/propiedades/${row.id}`)}
            />
          </div>
        )}
      </div>

      {/* Modal para Agregar/Editar Propiedad */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingProperty ? 'Editar Propiedad' : editingDataLoading ? 'Cargando...' : 'Agregar Nueva Propiedad'}
        size="xl"
      >
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}
        {editingDataLoading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-4">
            <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-gray-600">Cargando datos de la propiedad...</p>
          </div>
        ) : (
          <PropertyForm
            property={editingProperty}
            propertyUnits={editingPropertyUnits}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isSubmitting={isSubmitting}
          />
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={isDeleteModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        message={
          <>
            ¿Seguro que deseas eliminar la propiedad <strong>{propertyToDelete?.name}</strong>?
          </>
        }
        isLoading={deletingId !== null}
      />

      {/* Toast de notificación */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

